import React, { createContext, useContext, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useHostCards, useUIEvents, type UseHostCards } from "@codenexus/react";
import type { HostCardProps } from "@codenexus/react-mui";
import IngestionCard from "./IngestionCard";
import type { AppChatEventMap } from "../chatTools/types";

/** Name under which the ingestion transcript card is registered on
 *  <CodeNexusChat cards={{ [KNOWLEDGE_INGESTION_CARD]: KnowledgeIngestionHostCard }} />. */
export const KNOWLEDGE_INGESTION_CARD = "knowledge-ingestion";

interface IngestionSummary {
    indexed: { fileId: number; filename: string }[];
    failed: { filename: string; error: string }[];
}

/**
 * Host-card `data` must stay serializable (it can be persisted/replayed), so
 * the File objects the composer captured on drop/paste/paperclip never go
 * into `data` — they live in this in-memory, per-session registry keyed by
 * `batchId`, and the card component looks them up via
 * `IngestionBatchRegistryContext` while it's rendering "live". Once the
 * registry no longer has the batch (e.g. page reload), the card falls back to
 * whatever `data.summary` was recorded via `useHostCards().update(...)`.
 */
export interface IngestionBatchRegistry {
    put(batchId: string, files: File[]): void;
    /** Returns the files for a batch and keeps them (idempotent get) — the
     *  card may re-render multiple times while the batch is in flight. */
    take(batchId: string): File[] | undefined;
    /** Drops every batch's File refs. Called on session switch/clear (when
     *  the host's card list goes from non-empty to empty) so orphaned batches
     *  don't pin File objects in memory after their cards are gone. */
    clear(): void;
}

export function createIngestionBatchRegistry(): IngestionBatchRegistry {
    const store = new Map<string, File[]>();
    return {
        put(batchId, files) {
            store.set(batchId, files);
        },
        take(batchId) {
            return store.get(batchId);
        },
        clear() {
            store.clear();
        }
    };
}

export const IngestionBatchRegistryContext = createContext<IngestionBatchRegistry | null>(null);

/** Render-nothing child of <CodeNexusChat>: captures useHostCards() into a
 *  host ref so imperative code outside the chat tree (e.g. the composer's
 *  onFilesAdded handler) can append/update transcript cards.
 *
 *  Optionally evicts an `IngestionBatchRegistry` on session switch/clear.
 *  Eviction is driven purely by the `cards` array transitioning from
 *  length > 0 to length 0 — cards are never removed individually in this
 *  app, so that transition only happens when the whole session is
 *  cleared/switched. Watching state (not a mount/unmount lifecycle) keeps
 *  this StrictMode-safe: the synthetic mount→cleanup→mount cycle never
 *  observes a cards-length transition, so it never fires a spurious clear(). */
export function ExposeHostCards({
    apiRef,
    registry
}: {
    apiRef: React.MutableRefObject<UseHostCards | null>;
    registry?: IngestionBatchRegistry;
}): null {
    const hostCards = useHostCards();
    useEffect(() => {
        apiRef.current = hostCards;
        return () => {
            apiRef.current = null;
        };
    }, [apiRef, hostCards]);

    const prevLenRef = useRef(hostCards.cards.length);
    useEffect(() => {
        const len = hostCards.cards.length;
        if (prevLenRef.current > 0 && len === 0) {
            registry?.clear();
        }
        prevLenRef.current = len;
    }, [hostCards.cards.length, registry]);

    return null;
}

// NOTE: this note-building logic intentionally mirrors
// chatTools/uploadFileToKnowledgeBase.tsx's onSettled handler (same note
// shape for the `knowledge_files_ingested` context note). It is duplicated
// rather than imported/extracted so that module's existing tests don't need
// to change.
function buildIngestionNote(summary: IngestionSummary): string {
    const indexedNotes = summary.indexed.map(f => `"${f.filename}" (fileId ${f.fileId})`);
    const failedNotes = summary.failed.map(f => `"${f.filename}" (${f.error})`);
    const parts: string[] = [];
    if (indexedNotes.length > 0) {
        parts.push(`Indexed and searchable: ${indexedNotes.join(", ")}.`);
    }
    if (failedNotes.length > 0) {
        parts.push(`Failed to ingest: ${failedNotes.join(", ")}.`);
    }
    return `The in-chat uploader finished. ${parts.join(" ")}`.trim();
}

interface AbortedSummary {
    indexed: { fileId: number; filename: string }[];
    incomplete: string[];
}

// NOTE: mirrors chatTools/uploadFileToKnowledgeBase.tsx's onAborted handler
// (same note shape for the `knowledge_ingestion_interrupted` context note).
// Duplicated rather than shared so neither module's tests need to change.
function buildInterruptedNote(summary: AbortedSummary): string {
    const indexedNotes = summary.indexed.map(f => `"${f.filename}" (fileId ${f.fileId})`);
    const incompleteNotes = summary.incomplete.map(name => `"${name}"`);
    const parts: string[] = [];
    if (indexedNotes.length > 0) {
        parts.push(`Already indexed and searchable: ${indexedNotes.join(", ")}.`);
    }
    if (incompleteNotes.length > 0) {
        parts.push(`Not completed: ${incompleteNotes.join(", ")}.`);
    }
    return `Ingestion was interrupted by a chat-session change. ${parts.join(" ")}`.trim();
}

function summaryLine(summary: IngestionSummary): string {
    const indexedPart = `${summary.indexed.length} file${summary.indexed.length === 1 ? "" : "s"} indexed`;
    if (summary.failed.length === 0) return `${indexedPart}.`;
    return `${indexedPart}, ${summary.failed.length} failed.`;
}

interface KnowledgeIngestionCardData {
    batchId?: string;
    /** Current product/project id captured when the card was created
     *  (KAN-1584 follow-up) — passed through to IngestionCard so uploads
     *  auto-scope to the product that was current at drop/paste/paperclip
     *  time. Absent when there was no current product. */
    projectId?: number;
    summary?: IngestionSummary;
}

/** Registered under KNOWLEDGE_INGESTION_CARD. `data`: { batchId: string, projectId?: number }. */
export function KnowledgeIngestionHostCard({ id, data }: HostCardProps): React.ReactElement {
    const registry = useContext(IngestionBatchRegistryContext);
    const bus = useUIEvents<AppChatEventMap>();
    const { update } = useHostCards();
    const cardData = data as KnowledgeIngestionCardData;
    const batchId = typeof cardData.batchId === "string" ? cardData.batchId : undefined;
    const projectId = typeof cardData.projectId === "number" ? cardData.projectId : undefined;
    const files = batchId ? registry?.take(batchId) : undefined;

    if (files) {
        return (
            <IngestionCard
                initialFiles={files}
                projectId={projectId}
                onFileIndexed={r => {
                    bus.emit("knowledge_file_uploaded", r);
                }}
                onSettled={summary => {
                    bus.pushContext({
                        knowledge_files_ingested: summary,
                        note: buildIngestionNote(summary)
                    });
                    update(id, { summary });
                }}
                onAborted={summary => {
                    bus.pushContext({
                        knowledge_ingestion_interrupted: summary,
                        note: buildInterruptedNote(summary)
                    });
                }}
            />
        );
    }

    // Defensive fallback: the in-memory registry is cleared (e.g. page
    // reload) but the card is still in the transcript. Never crash — render
    // whatever outcome was recorded, or a neutral closing line.
    if (cardData.summary) {
        return (
            <Box sx={{ p: "8px 4px" }}>
                <Typography sx={{ fontSize: "12.5px", color: "text.secondary" }}>{summaryLine(cardData.summary)}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: "8px 4px" }}>
            <Typography sx={{ fontSize: "12.5px", color: "text.secondary" }}>Ingestion session ended.</Typography>
        </Box>
    );
}
